import React, { useState } from "react";
import "./ContactUs.css";
import Logo from "./Logo";
import {
  FaFacebook,
  FaLinkedin,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhone,
  FaClock,
  FaPaperPlane,
} from "react-icons/fa";
import useInView from "../hooks/useInView";

/* ------------------------------------------------------------------
 *  CONTACT FORM SETUP
 *  ----------------------------------------------------------------
 *  This form submits to Formspree (free tier, no backend needed).
 *
 *  How to wire it up:
 *    1.  Go to https://formspree.io and sign up (free)
 *    2.  Create a new form → copy your endpoint, it looks like:
 *        https://formspree.io/f/abcdwxyz
 *    3.  Paste it into FORMSPREE_ENDPOINT below
 *
 *  If you'd rather use EmailJS, Netlify Forms, or your own backend,
 *  just change the submit logic in handleSubmit().
 * ----------------------------------------------------------------- */
const FORMSPREE_ENDPOINT = "https://formspree.io/f/YOUR_FORM_ID";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const [formRef, formInView] = useInView();
  const [infoRef, infoInView] = useInView();

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        setStatus("success");
        setFormData({ name: "", email: "", phone: "", service: "", message: "" });
      } else {
        const data = await res.json().catch(() => ({}));
        setErrorMsg(
          data?.errors?.[0]?.message ||
            "We couldn't send your message. Please try emailing us directly."
        );
        setStatus("error");
      }
    } catch (err) {
      setErrorMsg("Network error. Please try emailing us directly.");
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="contact-wrapper">
      {/* ============= CONTACT FORM SECTION ============= */}
      <div className="contact-form-section">
        <div className="contact-form-inner">
          <div
            ref={infoRef}
            className={`contact-info-side ${infoInView ? "in-view" : ""}`}
          >
            <span className="contact-eyebrow">Get In Touch</span>
            <h2>Let's talk about your project</h2>
            <p className="contact-lead">
              Have a project in mind or just want to know more about how we
              can help? Drop us a message — we typically reply within 24 hours.
            </p>

            <ul className="contact-info-list">
              <li>
                <span className="info-icon"><FaMapMarkerAlt /></span>
                <div>
                  <strong>Office</strong>
                  <span>
                    KK Market, Dhankawadi, Pune - 411043, India
                  </span>
                </div>
              </li>
              <li>
                <span className="info-icon"><FaEnvelope /></span>
                <div>
                  <strong>Email</strong>
                  <a href="mailto:info@vezrontechnologies.com">
                    info@vezrontechnologies.com
                  </a>
                </div>
              </li>
              <li>
                <span className="info-icon"><FaPhone /></span>
                <div>
                  <strong>Phone</strong>
                  <a href="tel:+912040111983">+91 9112915891</a>
                </div>
              </li>
              <li>
                <span className="info-icon"><FaClock /></span>
                <div>
                  <strong>Working Hours</strong>
                  <span>Mon – Sat: 8:00 AM – 5:00 PM</span>
                </div>
              </li>
            </ul>

            <div className="map-wrap">
              <iframe
                title="Vezron Technologies Office Location"
                src="https://maps.google.com/maps?q=KK+Market+Dhankawadi+Pune&t=&z=15&ie=UTF8&iwloc=&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className={`contact-form ${formInView ? "in-view" : ""}`}
            noValidate
            autoComplete="off"
          >
            <h3>Send us a message</h3>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="name">Full Name *</label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  autoComplete="off"
                />
              </div>
              <div className="form-field">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="phone">Phone Number</label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 / +1 ..."
                  autoComplete="off"
                />
              </div>
              <div className="form-field">
                <label htmlFor="service">Service Interested In *</label>
                <select
                  id="service"
                  name="service"
                  required
                  value={formData.service}
                  onChange={handleChange}
                >
                  <option value="">Select a service</option>
                  <option value="Designing & Estimation">
                    Designing &amp; Estimation
                  </option>
                  <option value="Real Estate Services">
                    Real Estate Services
                  </option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="message">Your Message *</label>
              <textarea
                id="message"
                name="message"
                required
                rows="5"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your project, timeline, and what you need..."
              />
            </div>

            <button
              type="submit"
              className="form-submit"
              disabled={status === "loading"}
            >
              {status === "loading" ? (
                "Sending..."
              ) : (
                <>
                  Send Message <FaPaperPlane />
                </>
              )}
            </button>

            {status === "success" && (
              <p className="status-msg success">
                ✓ Thank you! Your message has been sent. We'll reply within 24
                hours.
              </p>
            )}
            {status === "error" && (
              <p className="status-msg error">{errorMsg}</p>
            )}
          </form>
        </div>
      </div>

      {/* ============= FOOTER ============= */}
      <footer className="contact-section">
        {/* Ambient decorative layers */}
        <span className="footer-orb orb-1" />
        <span className="footer-orb orb-2" />
        <span className="footer-orb orb-3" />
        <span className="footer-grid" />

        {/* Top divider — gradient line */}
        <div className="footer-divider" />

        {/* Newsletter strip */}
        <div className="footer-newsletter">
          <div className="newsletter-inner">
            <div className="newsletter-text">
              <h3>
                Stay in the loop
              </h3>
              <p>Get insights on cost estimation, real-estate trends, and project updates — straight to your inbox.</p>
            </div>
            <form
              className="newsletter-form"
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                // Hook this up to your mailing list provider (Mailchimp, ConvertKit etc.) later.
                const email = e.target.elements.email.value;
                alert(`Thanks! We'll keep ${email} posted.`);
                e.target.reset();
              }}
            >
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                required
                aria-label="Email address"
              />
              <button type="submit">
                Subscribe <FaPaperPlane />
              </button>
            </form>
          </div>
        </div>

        <div className="footer-container">
          <div className="footer-column footer-brand-col">
            <div className="footer-logo-wrap">
              <Logo height={48} />
            </div>
            <p className="footer-text-block">
              Vezron Technologies delivers reliable designing, estimation, and
              real estate services to businesses across India and the USA. We
              focus on accuracy, speed, and long-term partnerships.
            </p>
            <div className="social-icons">
              <a
                href="https://www.facebook.com/profile.php?id=61575025443739"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <FaFacebook />
              </a>
              <a
                href="https://www.linkedin.com/company/vezron-technologies/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <FaLinkedin />
              </a>
            </div>
          </div>

          <div className="footer-column">
            <h3 className="footer-heading">Quick Contact</h3>
            <p className="footer-subheading">Vezron Technologies Pvt. Ltd.</p>
            <a href="https://maps.google.com/maps?q=KK+Market+Dhankawadi+Pune&t=&z=15&ie=UTF8&iwloc=&output=embed"
               target="_blank"
               rel="noopener noreferrer"
               className="footer-text footer-link">
              <FaMapMarkerAlt />KK Market, Dhankawadi, Pune - 411043, India


            </a>
            <a href="mailto:info@vezrontechnologies.com" className="footer-text footer-link">
              <FaEnvelope /> info@vezrontechnologies.com
            </a>
            <a href="tel:+912040111983" className="footer-text footer-link">
              <FaPhone /> +91 9112915891
            </a>
          </div>
        </div>

        <div className="footer-bottom">
          <p>
            © 2026 Vezron Technologies Pvt. Ltd. All Rights Reserved.
          </p>
          <p className="footer-bottom-tag">
            Designed for builders, developers &amp; real-estate teams across India and the USA.
          </p>
        </div>
      </footer>
    </section>
  );
};

export default ContactUs;